import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  LogOut,
  Download,
  ExternalLink,
  Check,
  X,
} from "lucide-react";
import { api, API, extractErrorMessage } from "@/lib/api";

const LOGO_URL =
  "https://sonarbanglahaat.com/wp-content/uploads/2025/12/cropped-Logo-Photoroom-1.png";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

async function downloadDocument(fileId, filename) {
  const token = localStorage.getItem("sbh_admin_token");
  const res = await fetch(`${API}/admin/documents/${fileId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    toast.error("Failed to download document");
    return;
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "document";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function VendorList({ items, onOpen, activeId }) {
  return (
    <div className="w-full">
      <div className="grid grid-cols-[1.4fr_1fr_1fr_0.8fr_0.7fr] gap-4 px-4 py-3 border-b border-[color:var(--sbh-border)] text-[11px] tracking-[0.2em] uppercase text-[color:var(--sbh-muted)]">
        <span>Brand · Owner</span>
        <span>Category</span>
        <span>Location</span>
        <span>Submitted</span>
        <span>Status</span>
      </div>
      {items.length === 0 && (
        <div className="px-4 py-10 text-center text-[color:var(--sbh-muted)] font-editorial italic text-[18px]">
          No applications yet.
        </div>
      )}
      {items.map((v) => {
        const isActive = v.id === activeId;
        return (
          <button
            key={v.id}
            type="button"
            onClick={() => onOpen(v.id)}
            className={`w-full text-left grid grid-cols-[1.4fr_1fr_1fr_0.8fr_0.7fr] gap-4 px-4 py-4 border-b border-[color:var(--sbh-border)] hover:bg-[color:var(--sbh-cream-2)] transition-colors ${
              isActive ? "bg-[color:var(--sbh-cream-2)]" : ""
            }`}
            data-testid={`vendor-row-${v.id}`}
          >
            <div>
              <div className="font-editorial text-[19px] text-[color:var(--sbh-ink)] leading-tight">
                {v.brand_name || "—"}
              </div>
              <div className="text-[12.5px] text-[color:var(--sbh-ink-2)] mt-0.5">
                {v.owner_name} · {v.email}
              </div>
            </div>
            <div className="text-[13px] text-[color:var(--sbh-ink-2)] self-center">
              {v.product_category || "—"}
              {v.product_categories && v.product_categories.length > 1 && (
                <span className="ml-2 text-[10.5px] tracking-[0.16em] uppercase text-[color:var(--sbh-terracotta)]">
                  +{v.product_categories.length - 1}
                </span>
              )}
            </div>
            <div className="text-[13px] text-[color:var(--sbh-ink-2)] self-center">
              {[v.district, v.state].filter(Boolean).join(", ") || "—"}
            </div>
            <div className="text-[12.5px] text-[color:var(--sbh-ink-2)] self-center">
              {fmtDate(v.submitted_at || v.created_at)}
            </div>
            <div className="self-center">
              <StatusPill status={v.status || "draft"} />
            </div>
          </button>
        );
      })}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    submitted: {
      cls: "border-[color:var(--sbh-terracotta)] text-[color:var(--sbh-terracotta)]",
      label: "submitted",
    },
    approved: {
      cls: "border-[color:var(--sbh-mustard)] text-[color:var(--sbh-mustard)] bg-[color:var(--sbh-mustard)]/10",
      label: "approved",
    },
    rejected: {
      cls: "border-[color:var(--sbh-deep-red)] text-[color:var(--sbh-deep-red)]",
      label: "rejected",
    },
    onboarded: {
      cls: "border-[color:var(--sbh-ink)] text-[color:var(--sbh-cream)] bg-[color:var(--sbh-ink)]",
      label: "onboarded",
    },
    draft: {
      cls: "border-[color:var(--sbh-border)] text-[color:var(--sbh-muted)]",
      label: "draft",
    },
  };
  const cfg = map[status] || map.draft;
  return (
    <span
      className={`inline-flex items-center text-[10.5px] tracking-[0.18em] uppercase px-2 py-1 border ${cfg.cls}`}
    >
      {cfg.label}
    </span>
  );
}

function DetailField({ label, value }) {
  return (
    <div className="grid grid-cols-[160px_1fr] gap-4 py-3 border-b border-[color:var(--sbh-border)]">
      <div className="eyebrow text-[color:var(--sbh-muted)]">{label}</div>
      <div className="text-[14px] text-[color:var(--sbh-ink)] break-words">
        {value || <span className="text-[color:var(--sbh-muted)]">—</span>}
      </div>
    </div>
  );
}

function VendorDetail({ id, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(null); // "approve" | "reject" | null
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = () => {
    setLoading(true);
    Promise.all([
      api.get(`/admin/vendors/${id}`),
      api.get(`/admin/vendors/${id}/audit`),
    ])
      .then(([detailRes, auditRes]) => {
        setData(detailRes.data);
        setAudit(auditRes.data?.events || []);
      })
      .catch((e) => toast.error(extractErrorMessage(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh();
  }, [id]);

  const performAction = async () => {
    if (!action) return;
    setBusy(true);
    try {
      const url = `/admin/vendors/${id}/${action}`;
      const body = action === "reject" ? { reason } : { note: reason };
      const { data: res } = await api.post(url, body);
      toast.success(
        action === "approve"
          ? "Approved — payout link emailed to vendor."
          : "Application rejected — email sent.",
      );
      if (action === "approve" && res?.payout_link) {
        try {
          await navigator.clipboard?.writeText(res.payout_link);
          toast.info("Payout link also copied to your clipboard.");
        } catch {
          /* no-op */
        }
      }
      setAction(null);
      setReason("");
      refresh();
      onChanged?.();
    } catch (e) {
      toast.error(extractErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-[color:var(--sbh-muted)] font-editorial italic">
        Loading…
      </div>
    );
  }
  if (!data) return null;

  const docs = data.documents || {};
  const canApprove = data.status === "submitted" || data.status === "rejected";
  const canReject = data.status === "submitted" || data.status === "approved";
  const payout = data.payout;

  return (
    <div className="p-6 md:p-8" data-testid={`vendor-detail-${id}`}>
      <button
        onClick={onClose}
        className="btn-ghost mb-6"
        data-testid="vendor-detail-back"
      >
        <ArrowLeft className="w-4 h-4" strokeWidth={2} /> Back to list
      </button>

      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="flex items-center gap-3">
            <span className="eyebrow">Application</span>
            <StatusPill status={data.status} />
          </div>
          <h2 className="mt-3 font-editorial text-[36px] md:text-[44px] leading-[1.05] tracking-[-0.01em]">
            {data.business?.brand_name}
          </h2>
          <p className="mt-1 text-[color:var(--sbh-ink-2)]">
            {data.business?.owner_name} · {data.email}
          </p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {canApprove && (
            <button
              className="btn-editorial"
              onClick={() => setAction("approve")}
              disabled={busy}
              data-testid="vendor-approve-btn"
            >
              <Check className="w-4 h-4" strokeWidth={2} /> Approve
            </button>
          )}
          {canReject && (
            <button
              type="button"
              onClick={() => setAction("reject")}
              disabled={busy}
              className="inline-flex items-center gap-2 px-6 py-4 text-[13px] tracking-[0.16em] uppercase font-medium text-[color:var(--sbh-deep-red)] border border-[color:var(--sbh-deep-red)] hover:bg-[color:var(--sbh-deep-red)] hover:text-[color:var(--sbh-cream)] transition-colors"
              data-testid="vendor-reject-btn"
            >
              <X className="w-4 h-4" strokeWidth={2} /> Reject
            </button>
          )}
        </div>
      </div>

      {/* Action modal (inline) */}
      {action && (
        <div className="mt-6 border border-[color:var(--sbh-terracotta)] bg-[color:var(--sbh-cream-2)] p-5 md:p-6" data-testid="vendor-action-panel">
          <div className="eyebrow">
            {action === "approve" ? "Approve application" : "Reject application"}
          </div>
          <p className="mt-2 text-[14px] text-[color:var(--sbh-ink-2)] max-w-xl">
            {action === "approve"
              ? "We'll email the vendor a private, secure link to add their payout bank details."
              : "The vendor will receive an email with your note. This can be reversed by approving later."}
          </p>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={
              action === "approve"
                ? "Optional internal note (not shown to vendor)"
                : "Reason (shown to vendor, optional)"
            }
            rows={3}
            maxLength={400}
            className="mt-4 w-full bg-transparent border border-[color:var(--sbh-border)] focus:border-[color:var(--sbh-terracotta)] outline-none p-3 text-[14px] font-body"
            data-testid="vendor-action-note"
          />
          <div className="mt-4 flex items-center gap-3">
            <button
              className="btn-editorial"
              onClick={performAction}
              disabled={busy}
              data-testid="vendor-action-confirm"
            >
              {busy ? "Working…" : `Confirm ${action}`}
            </button>
            <button
              className="btn-ghost"
              onClick={() => {
                setAction(null);
                setReason("");
              }}
              disabled={busy}
              data-testid="vendor-action-cancel"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <section className="mt-8">
        <div className="eyebrow">Business</div>
        <div className="mt-3">
          <DetailField label="Mobile" value={data.business?.mobile} />
          <DetailField label="WhatsApp" value={data.business?.whatsapp} />
          <DetailField
            label="Categories"
            value={
              (data.business?.product_categories &&
                data.business.product_categories.join(", ")) ||
              data.business?.product_category
            }
          />
          <DetailField label="Brand Story" value={data.business?.brand_story} />
        </div>
      </section>

      <section className="mt-8">
        <div className="eyebrow">Address</div>
        <div className="mt-3">
          <DetailField label="Address 1" value={data.address?.address_line_1} />
          <DetailField label="Address 2" value={data.address?.address_line_2} />
          <DetailField label="District" value={data.address?.district} />
          <DetailField label="State" value={data.address?.state} />
          <DetailField label="Pin Code" value={data.address?.pin_code} />
        </div>
      </section>

      <section className="mt-8">
        <div className="eyebrow">Tax</div>
        <div className="mt-3">
          <DetailField label="GST Registered" value={data.tax?.gst_registered ? "Yes" : "No"} />
          <DetailField label="GSTIN" value={data.tax?.gstin} />
          <DetailField label="PAN Number" value={data.tax?.pan_number} />
        </div>
      </section>

      <section className="mt-8">
        <div className="eyebrow">Documents</div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          {["pan", "cheque", "gst"].map((k) => {
            const d = docs[k];
            if (!d) return null;
            return (
              <div key={k} className="border border-[color:var(--sbh-border)] p-4">
                <div className="eyebrow text-[color:var(--sbh-ink-2)] uppercase">
                  {k === "pan"
                    ? "PAN"
                    : k === "cheque"
                    ? "Cheque / Passbook"
                    : "GST Certificate"}
                </div>
                <div className="mt-2 text-[13px] break-words">{d.filename}</div>
                <div className="text-[11px] tracking-[0.16em] uppercase text-[color:var(--sbh-muted)] mt-1">
                  {(d.size / 1024).toFixed(0)} KB · {(d.content_type || "").split("/").pop()}
                </div>
                <button
                  className="btn-editorial mt-4 w-full justify-center"
                  onClick={() => downloadDocument(d.file_id, d.filename)}
                  data-testid={`download-${k}`}
                >
                  <Download className="w-4 h-4" strokeWidth={2} />
                  Download
                </button>
              </div>
            );
          })}
        </div>
      </section>

      {payout && (
        <section className="mt-8" data-testid="vendor-payout-section">
          <div className="eyebrow">Payout Details</div>
          <div className="mt-3">
            <DetailField label="Account Holder" value={payout.account_holder_name} />
            <DetailField label="Bank Name" value={payout.bank_name} />
            <DetailField label="Account Number" value={payout.account_number} />
            <DetailField label="IFSC" value={payout.ifsc} />
            <DetailField label="UPI ID" value={payout.upi_id} />
            <DetailField label="Submitted At" value={fmtDate(payout.submitted_at)} />
          </div>
        </section>
      )}

      <section className="mt-8">
        <div className="eyebrow">Timeline</div>
        <div className="mt-3">
          <DetailField label="Submitted" value={fmtDate(data.submitted_at)} />
          <DetailField label="Approved" value={fmtDate(data.approved_at)} />
          <DetailField label="Rejected" value={fmtDate(data.rejected_at)} />
          <DetailField label="Onboarded" value={fmtDate(data.onboarded_at)} />
          {data.rejection_reason ? (
            <DetailField label="Rejection Reason" value={data.rejection_reason} />
          ) : null}
        </div>
      </section>

      <section className="mt-8" data-testid="vendor-audit-section">
        <div className="flex items-baseline gap-4">
          <div className="eyebrow">Audit Trail</div>
          <div className="text-[11px] tracking-[0.16em] uppercase text-[color:var(--sbh-muted)]">
            {audit.length} event{audit.length === 1 ? "" : "s"}
          </div>
        </div>
        {audit.length === 0 ? (
          <div className="mt-3 text-[13px] text-[color:var(--sbh-muted)] italic font-editorial">
            No events recorded yet.
          </div>
        ) : (
          <ol className="mt-4 relative border-l border-[color:var(--sbh-border)] pl-6 md:pl-8 space-y-6">
            {audit.map((e) => (
              <li key={e.id} className="relative" data-testid={`audit-event-${e.event}`}>
                <span
                  className={`absolute -left-[7px] top-1 w-3 h-3 rounded-full border ${
                    e.event === "approved"
                      ? "bg-[color:var(--sbh-mustard)] border-[color:var(--sbh-mustard)]"
                      : e.event === "rejected"
                      ? "bg-[color:var(--sbh-deep-red)] border-[color:var(--sbh-deep-red)]"
                      : e.event === "onboarded"
                      ? "bg-[color:var(--sbh-ink)] border-[color:var(--sbh-ink)]"
                      : "bg-[color:var(--sbh-cream)] border-[color:var(--sbh-terracotta)]"
                  }`}
                />
                <div className="flex items-baseline justify-between gap-4 flex-wrap">
                  <div className="font-editorial text-[19px] text-[color:var(--sbh-ink)] capitalize">
                    {e.event.replace(/_/g, " ")}
                  </div>
                  <div className="text-[11px] tracking-[0.16em] uppercase text-[color:var(--sbh-muted)]">
                    {fmtDate(e.at)}
                  </div>
                </div>
                <div className="mt-1 text-[13px] text-[color:var(--sbh-ink-2)]">
                  <span className="uppercase tracking-[0.16em] text-[10px] text-[color:var(--sbh-terracotta)] mr-2">
                    {e.actor}
                  </span>
                  {e.actor_email || <em>system</em>}
                </div>
                {e.meta && Object.keys(e.meta).length > 0 && (
                  <div className="mt-2 text-[12px] text-[color:var(--sbh-muted)] font-mono flex flex-wrap gap-x-4 gap-y-1">
                    {Object.entries(e.meta).map(([k, v]) => (
                      <span key={k}>
                        <span className="text-[color:var(--sbh-ink-2)]">{k}:</span>{" "}
                        {typeof v === "string"
                          ? v || <em className="text-[color:var(--sbh-muted)]">—</em>
                          : String(v)}
                      </span>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const params = useParams();
  const [me, setMe] = useState(null);
  const [items, setItems] = useState([]);
  const [openId, setOpenId] = useState(params.id || null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("submitted");

  useEffect(() => {
    api
      .get("/admin/me")
      .then((r) => setMe(r.data))
      .catch(() => {
        localStorage.removeItem("sbh_admin_token");
        nav("/admin/login");
      });
  }, [nav]);

  const refresh = () => {
    setLoading(true);
    api
      .get("/admin/vendors", { params: filter === "all" ? {} : { status_filter: filter } })
      .then((r) => setItems(r.data.items || []))
      .catch((e) => toast.error(extractErrorMessage(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (me) refresh();
  }, [me, filter]);

  const logout = () => {
    localStorage.removeItem("sbh_admin_token");
    localStorage.removeItem("sbh_admin_email");
    nav("/admin/login");
  };

  if (!me) return null;

  return (
    <div className="min-h-screen w-full bg-[color:var(--sbh-cream)] relative">
      <div className="grain fixed inset-0 pointer-events-none z-[5]" />
      <header className="border-b border-[color:var(--sbh-border)] px-6 md:px-10 py-5 flex items-center justify-between relative z-10">
        <Link to="/" className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Sonar Bangla Haat" className="h-10 w-auto" />
        </Link>
        <div className="flex items-center gap-6">
          <div className="hidden md:block text-right">
            <div className="eyebrow">Signed in</div>
            <div className="text-[13px] text-[color:var(--sbh-ink)]">{me.email}</div>
          </div>
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost"
            data-testid="admin-view-site"
          >
            <ExternalLink className="w-4 h-4" strokeWidth={2} /> View Onboarding
          </a>
          <button className="btn-ghost" onClick={logout} data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4" strokeWidth={2} /> Logout
          </button>
        </div>
      </header>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="eyebrow">Sonar Bangla Haat · Applications</div>
            <h1 className="font-editorial text-[36px] md:text-[44px] leading-[1.05] tracking-[-0.01em]">
              Vendor Applications
            </h1>
          </div>
          <div className="gst-toggle" data-testid="status-filter">
            {["submitted", "approved", "onboarded", "rejected", "draft", "all"].map((s) => (
              <button
                key={s}
                className={filter === s ? "on" : ""}
                onClick={() => setFilter(s)}
                data-testid={`filter-${s}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {openId ? (
          <div className="border border-[color:var(--sbh-border)] bg-[color:var(--sbh-cream)]">
            <VendorDetail
              id={openId}
              onClose={() => setOpenId(null)}
              onChanged={refresh}
            />
          </div>
        ) : (
          <div className="border border-[color:var(--sbh-border)] bg-[color:var(--sbh-cream)]">
            {loading ? (
              <div className="p-10 text-center font-editorial italic text-[color:var(--sbh-muted)]">
                Loading…
              </div>
            ) : (
              <VendorList items={items} onOpen={setOpenId} activeId={openId} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
